import { createFileRoute } from "@tanstack/react-router"
import type { PayoutAvailability, PayoutBalanceResponse, PayoutFleetEntry } from "@nova-ai/shared"

import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { MarketplaceMark } from "../components/marketplaces/marketplace-mark"
import { PayoutsSection } from "../components/payouts/payouts-section"
import { humanizeMarketplaceId } from "../lib/marketplace"
import type { ExchangeRates } from "../lib/money"
import { byLifetimePaid } from "../lib/payout-order"
import { usePayoutBalances, usePayoutFleet, usePayoutsSync } from "../services/use-payouts"
import { useRates } from "../services/use-rates"

/** Why a marketplace can never have a payout cycle, in the words of whoever owns
 *  that fact. Both are configuration truths rather than failures, which is why the
 *  page states them once at the foot instead of giving each an empty card. */
const NO_SOURCE_REASON: Partial<Record<PayoutAvailability, string>> = {
  gated: "No shop id in its config — payouts are never read for it",
  unsupported: "Its platform publishes no payouts",
}

/** The reason this marketplace can never have a cycle, or `undefined` when it
 *  can — which is also how the page decides block or collapsed row. */
function noSourceReason(availability: PayoutAvailability): string | undefined {
  return NO_SOURCE_REASON[availability]
}

/**
 * Payouts across the fleet: one block per marketplace that can have settlement
 * cycles, biggest channel first, each showing its latest cycles and expanding to
 * everything Nova has mirrored.
 *
 * Order is by **lifetime paid**, converted to EUR — the marketplaces sell in EUR,
 * CZK and PLN, so a raw amount would rank a PLN channel above a larger EUR one.
 * Lifetime paid rather than what is owed right now: it measures how big a channel
 * is, and it barely moves, so a block stays where it was yesterday. Ranking on
 * `payable` would reshuffle the page three times a month, every time a payout
 * lands and drops that marketplace to zero.
 *
 * Deliberately absent: a fleet total. Three currencies folded into one headline is
 * a figure no seller portal shows, and it invites the question of which number is
 * authoritative — the confusion the per-marketplace band exists to end. There is
 * also no fleet Refresh: the sweep behind it is a sequential, budgeted walk built
 * for a cron with a report, and the 07:45 run already owns freshness.
 */
function PayoutsPage() {
  const fleet = usePayoutFleet()
  const balances = usePayoutBalances()
  const rates = useRates()

  const balanceByMarketplace = new Map(
    (balances.data?.balances ?? []).map((entry) => [entry.marketplace, entry])
  )
  const entries = fleet.data?.marketplaces ?? []
  const withPayouts = entries
    .filter((entry) => noSourceReason(entry.availability) === undefined)
    .sort(byLifetimePaid(balanceByMarketplace, rates.data))
  const withoutPayouts = entries.filter((entry) => noSourceReason(entry.availability) !== undefined)

  return (
    <AppShell
      title="Payouts"
      description="Marketplace settlement cycles and account balances across the fleet."
      fullWidth
    >
      <QueryState
        // Rates matter for more than display here: they decide the order, so
        // rendering before they land would show one order and then resort.
        isLoading={fleet.isLoading || rates.isLoading}
        error={fleet.error}
        isEmpty={entries.length === 0}
        emptyMessage="No marketplaces are configured."
      >
        <div className="space-y-6">
          {withPayouts.map((entry) => (
            <MarketplacePayouts
              key={entry.marketplace}
              entry={entry}
              balance={balanceByMarketplace.get(entry.marketplace)}
              rates={rates.data}
            />
          ))}

          {withoutPayouts.length > 0 ? <NoSourceList entries={withoutPayouts} /> : null}
        </div>
      </QueryState>
    </AppShell>
  )
}

/** One marketplace's block. The sync mutation lives here rather than on the page
 *  so each block owns its own in-flight and failed state — one marketplace
 *  refreshing must not put five other blocks in a syncing state. */
function MarketplacePayouts({
  entry,
  balance,
  rates,
}: {
  entry: PayoutFleetEntry
  balance: PayoutBalanceResponse | undefined
  rates: ExchangeRates | undefined
}) {
  const sync = usePayoutsSync(entry.marketplace)
  return (
    <PayoutsSection
      marketplace={entry.marketplace}
      data={entry}
      balance={balance}
      rates={rates}
      onSync={() => sync.mutate()}
      isSyncing={sync.isPending}
      syncFailed={sync.isError}
    />
  )
}

/** The marketplaces that will never have a cycle, named with the reason. Kept as
 *  one quiet list at the foot: an omitted marketplace is an absence with no reason
 *  attached, and a full empty card each would be five screens of nothing. */
function NoSourceList({ entries }: { entries: readonly PayoutFleetEntry[] }) {
  return (
    <section className="rounded-xl border border-border bg-card px-5 py-4 shadow-card">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Not tracked for payouts
      </h2>

      <ul className="mt-3 divide-y divide-border/60">
        {entries.map((entry) => (
          <li
            key={entry.marketplace}
            className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="flex items-center gap-2 text-sm text-foreground">
              <MarketplaceMark marketplace={entry.marketplace} />

              {humanizeMarketplaceId(entry.marketplace)}
            </span>

            <span className="text-xs text-muted-foreground">
              {noSourceReason(entry.availability) ?? "No payout source"}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export const Route = createFileRoute("/payouts")({ component: PayoutsPage })
